import React, { useState } from 'react';
import {
  Box,
  Text,
  Button,
  Table,
  TableHeader,
  TableHeaderRow,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  Badge,
  TableToolbar,
  TableToolbarActions,
  RefreshIcon,
  TablePagination,
  FileIcon,
  Amount,
} from '@razorpay/blade/components';
import { formatPaginationRange, getClaimStatusColor, getClaimStatusTitle } from './utils';
import { dateFormats, getFormattedDateValue } from 'utils/Dates';
import { EmptyView } from 'components/SalaryComponents/components/EmptyView';
import pluralize from 'pluralize';
import { FlexiClaimStatus } from './types';
import { ClaimDetailDrawer } from './ClaimDetailDrawer';
import { FlexiReimbursementClaim } from 'api/gql-utils/flexibleBenefits/types';
import { convertPaiseToRupees } from 'components/SalaryComponents/views/FlexibleBenefits/Claims/Edit/util';

interface CompletedProps {
  claimRequests: FlexiReimbursementClaim[];
  isLoading: boolean;
  isFetching: boolean;
  refetch: () => void;
  currentPage: number;
  itemsPerPage: number;
  setCurrentPage: (page: number) => void;
  setItemsPerPage: (limit: number) => void;
  statusFilter: string;
  totalCount: number;
}

export const Completed = ({
  claimRequests,
  isLoading,
  isFetching,
  refetch,
  currentPage,
  itemsPerPage,
  setCurrentPage,
  setItemsPerPage,
  statusFilter,
  totalCount,
}: CompletedProps) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedClaimId, setSelectedClaimId] = useState<string | null>(null);

  const handleOpenDrawer = (claimId: string) => {
    setSelectedClaimId(claimId);
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedClaimId(null);
  };

  const getEmptyStateMessage = () => {
    if (statusFilter === 'approved_claims' || statusFilter === 'approved_by_me') {
      return {
        title: 'No approved claims found',
        description: 'There are no approved claims to display',
      };
    }
    if (statusFilter === 'rejected_claims' || statusFilter === 'rejected_by_me') {
      return {
        title: 'No rejected claims found',
        description: 'There are no rejected claims to display',
      };
    }
    return {
      title: 'No completed requests found',
      description: 'There are no completed requests to display',
    };
  };

  const emptyState = getEmptyStateMessage();

  return (
    <Box marginTop="spacing.6">
      <Table
        isLoading={isLoading}
        isRefreshing={isFetching}
        gridTemplateColumns={
          claimRequests && claimRequests.length > 0
            ? '1.5fr 1fr 1fr 1fr 1.5fr 1fr 100px'
            : undefined
        }
        data={{
          nodes:
            claimRequests?.map((item) => ({
              id: item.claim_id,
              ...item,
            })) || [],
        }}
        pagination={
          claimRequests && claimRequests.length > 0 ? (
            <TablePagination
              currentPage={currentPage ?? 0}
              paginationType="server"
              showPageSizePicker={true}
              onPageChange={({ page }) => {
                setCurrentPage(page);
              }}
              onPageSizeChange={({ pageSize }) => {
                setItemsPerPage(pageSize);
                setCurrentPage(0);
              }}
              totalItemCount={totalCount}
            />
          ) : undefined
        }
        toolbar={
          <TableToolbar
            title={
              claimRequests && claimRequests.length > 0
                ? `Showing ${formatPaginationRange({
                    totalItems: totalCount,
                    currentPage,
                    itemsPerPage,
                  })} Completed Requests`
                : ''
            }>
            <TableToolbarActions>
              <Button
                accessibilityLabel="Refresh Completed Requests"
                icon={RefreshIcon}
                variant="tertiary"
                onClick={() => refetch()}
              />
            </TableToolbarActions>
          </TableToolbar>
        }>
        {(nodes) =>
          claimRequests && claimRequests.length > 0 ? (
            <>
              <TableHeader>
                <TableHeaderRow>
                  <TableHeaderCell>
                    <Text size="medium" weight="semibold">
                      Name
                    </Text>
                  </TableHeaderCell>
                  <TableHeaderCell>
                    <Text size="medium" weight="semibold">
                      Benefit type
                    </Text>
                  </TableHeaderCell>
                  <TableHeaderCell>
                    <Text size="medium" weight="semibold">
                      Amount
                    </Text>
                  </TableHeaderCell>
                  <TableHeaderCell>
                    <Text size="medium" weight="semibold">
                      Proof uploaded
                    </Text>
                  </TableHeaderCell>
                  <TableHeaderCell>
                    <Text size="medium" weight="semibold">
                      Description
                    </Text>
                  </TableHeaderCell>
                  <TableHeaderCell>
                    <Text size="medium" weight="semibold">
                      Expense date
                    </Text>
                  </TableHeaderCell>
                  <TableHeaderCell>
                    <Text size="medium" weight="semibold">
                      Status
                    </Text>
                  </TableHeaderCell>
                </TableHeaderRow>
              </TableHeader>
              <TableBody>
                {nodes.map((item) => (
                  <TableRow
                    key={item.id}
                    item={item}
                    onClick={() => handleOpenDrawer(item.id)}
                    hoverActions={
                      <Box display="flex" gap="spacing.4" alignItems="center">
                        <Button size="xsmall" onClick={() => handleOpenDrawer(item.id)}>
                          View Details
                        </Button>
                      </Box>
                    }>
                    <TableCell>
                      <Box display="flex" flexDirection="column" gap="spacing.1">
                        <Text size="medium" weight="regular">
                          {item.employee_name}
                        </Text>
                        <Text size="small" color="surface.text.gray.muted">
                          {item.employee_id}
                        </Text>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Text size="medium" weight="regular">
                        {item.component_display_name}
                      </Text>
                    </TableCell>
                    <TableCell>
                      <Amount
                        value={Number(
                          convertPaiseToRupees(
                            item.claim_status === 'REJECTED'
                              ? item.claim_amount
                              : item.approved_amount,
                          ),
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      <Badge icon={FileIcon} color="information">
                        {`${item.claim_evidence_id.length} `}
                        {pluralize('attachment', item.claim_evidence_id.length)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Text
                        size="medium"
                        weight="regular"
                        truncateAfterLines={1}
                        color="surface.text.gray.muted">
                        {item.claim_comments || '--'}
                      </Text>
                    </TableCell>
                    <TableCell>
                      <Text size="medium" weight="regular">
                        {item.expense_date
                          ? getFormattedDateValue({
                              date: new Date(
                                Number(item.expense_date) > 10000000000
                                  ? Number(item.expense_date)
                                  : Number(item.expense_date) * 1000,
                              ),
                              formatString: dateFormats.dateShortMonthCommaYear,
                            })
                          : '--'}
                      </Text>
                    </TableCell>
                    <TableCell>
                      <Badge color={getClaimStatusColor(item.claim_status as FlexiClaimStatus)}>
                        {getClaimStatusTitle(item.claim_status as FlexiClaimStatus)}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </>
          ) : (
            <EmptyView
              title={emptyState.title}
              description={
                <Text color="surface.text.gray.muted" weight="medium" textAlign="center">
                  {emptyState.description}
                </Text>
              }
            />
          )
        }
      </Table>

      <ClaimDetailDrawer
        isOpen={isDrawerOpen}
        onDismiss={handleCloseDrawer}
        claimId={selectedClaimId}
        viewOnly={true}
      />
    </Box>
  );
};
